import * as z from 'zod';

export const BulkCreateMessagesRequest = z.object({
  options: z
    .object({
      messages: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Message ID.')
              .max(40)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the message was created.')
              .optional()
              .nullable(),
            sender: z
              .enum(['ASSISTANT', 'USER', 'SYSTEM', 'SITE_VISITOR'])
              .optional(),
            conversationId: z
              .string()
              .describe('Conversation ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            answerTo: z
              .string()
              .describe('ID of the message that this message is answering.')
              .max(40)
              .optional()
              .nullable(),
            messageType: z.enum([
              'QUESTION',
              'ANSWER',
              'INTRO',
              'LEGAL',
              'CONTACT_FORM',
              'CONTACT_FORM_SUBMITTED',
              'SITE_OWNER_NOTIFICATION',
              'OFFLINE',
              'OUT_OF_QUOTA',
              'USER_ANSWER',
              'SITE_NAVIGATION',
              'NAVIGATION_ANSWER',
              'IGNORED_ANSWER',
              'VISITOR_OPTION_SELECTED',
              'ADD_TO_CART',
              'BOOK_SERVICE',
              'GENERATED_QUESTION',
              'SUGGESTED_QUESTION',
              'USER_QUESTION',
              'USER_QUESTION_ANSWER',
              'WIX_USER_MEDIA',
              'MEDIA',
              'WIX_USER_CARD',
              'WIX_USER_MINIMAL_BODY',
              'WIX_USER_FORM',
              'WIX_SYSTEM',
            ]),
            body: z
              .object({
                mainText: z
                  .string()
                  .describe('Main text of the message.')
                  .max(4096)
                  .optional()
                  .nullable(),
                footerText: z
                  .string()
                  .describe('Footer text of the message.')
                  .max(4096)
                  .optional()
                  .nullable(),
                additionalData: z
                  .array(z.record(z.string(), z.any()))
                  .max(30)
                  .optional(),
              })
              .describe('Message body.')
              .optional(),
            inboxId: z
              .string()
              .describe('Message Id in the Inbox')
              .max(40)
              .optional()
              .nullable(),
            visitorOptions: z.array(z.string()).max(10).optional(),
            assistantActions: z.array(z.string()).max(10).optional(),
            visitorOptionsFields: z
              .array(z.record(z.string(), z.any()))
              .max(50)
              .optional(),
            engineStatus: z
              .string()
              .describe('Exact status message returned by ai site chat engine,')
              .max(50)
              .optional()
              .nullable(),
          })
        )
        .min(1)
        .max(25)
        .optional(),
    })
    .optional(),
});
export const BulkCreateMessagesResponse = z.object({
  messages: z
    .array(
      z.object({
        _id: z.string().describe('Message ID.').max(40).optional().nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the message was created.')
          .optional()
          .nullable(),
        sender: z
          .enum(['ASSISTANT', 'USER', 'SYSTEM', 'SITE_VISITOR'])
          .describe('Message sender type.')
          .optional(),
        conversationId: z
          .string()
          .describe('Conversation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        answerTo: z
          .string()
          .describe('ID of the message that this message is answering.')
          .max(40)
          .optional()
          .nullable(),
        messageType: z
          .enum([
            'QUESTION',
            'ANSWER',
            'INTRO',
            'LEGAL',
            'CONTACT_FORM',
            'CONTACT_FORM_SUBMITTED',
            'SITE_OWNER_NOTIFICATION',
            'OFFLINE',
            'OUT_OF_QUOTA',
            'USER_ANSWER',
            'SITE_NAVIGATION',
            'NAVIGATION_ANSWER',
            'IGNORED_ANSWER',
            'VISITOR_OPTION_SELECTED',
            'ADD_TO_CART',
            'BOOK_SERVICE',
            'GENERATED_QUESTION',
            'SUGGESTED_QUESTION',
            'USER_QUESTION',
            'USER_QUESTION_ANSWER',
            'WIX_USER_MEDIA',
            'MEDIA',
            'WIX_USER_CARD',
            'WIX_USER_MINIMAL_BODY',
            'WIX_USER_FORM',
            'WIX_SYSTEM',
          ])
          .describe('Message type.')
          .optional(),
        body: z
          .object({
            mainText: z
              .string()
              .describe('Main text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            footerText: z
              .string()
              .describe('Footer text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            additionalData: z
              .array(z.record(z.string(), z.any()))
              .max(30)
              .optional(),
          })
          .describe('Message body.')
          .optional(),
        inboxId: z
          .string()
          .describe('Message Id in the Inbox')
          .max(40)
          .optional()
          .nullable(),
        visitorOptions: z.array(z.string()).max(10).optional(),
        assistantActions: z.array(z.string()).max(10).optional(),
        visitorOptionsFields: z
          .array(z.record(z.string(), z.any()))
          .max(50)
          .optional(),
        engineStatus: z
          .string()
          .describe('Exact status message returned by ai site chat engine,')
          .max(50)
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const ListMessagesRequest = z.object({
  options: z
    .object({
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
        )
        .optional(),
    })
    .describe('Additional options for listing messages.')
    .optional(),
});
export const ListMessagesResponse = z.object({
  messages: z
    .array(
      z.object({
        _id: z.string().describe('Message ID.').max(40).optional().nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the message was created.')
          .optional()
          .nullable(),
        sender: z
          .enum(['ASSISTANT', 'USER', 'SYSTEM', 'SITE_VISITOR'])
          .describe('Message sender type.')
          .optional(),
        conversationId: z
          .string()
          .describe('Conversation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        answerTo: z
          .string()
          .describe('ID of the message that this message is answering.')
          .max(40)
          .optional()
          .nullable(),
        messageType: z
          .enum([
            'QUESTION',
            'ANSWER',
            'INTRO',
            'LEGAL',
            'CONTACT_FORM',
            'CONTACT_FORM_SUBMITTED',
            'SITE_OWNER_NOTIFICATION',
            'OFFLINE',
            'OUT_OF_QUOTA',
            'USER_ANSWER',
            'SITE_NAVIGATION',
            'NAVIGATION_ANSWER',
            'IGNORED_ANSWER',
            'VISITOR_OPTION_SELECTED',
            'ADD_TO_CART',
            'BOOK_SERVICE',
            'GENERATED_QUESTION',
            'SUGGESTED_QUESTION',
            'USER_QUESTION',
            'USER_QUESTION_ANSWER',
            'WIX_USER_MEDIA',
            'MEDIA',
            'WIX_USER_CARD',
            'WIX_USER_MINIMAL_BODY',
            'WIX_USER_FORM',
            'WIX_SYSTEM',
          ])
          .describe('Message type.')
          .optional(),
        body: z
          .object({
            mainText: z
              .string()
              .describe('Main text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            footerText: z
              .string()
              .describe('Footer text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            additionalData: z
              .array(z.record(z.string(), z.any()))
              .max(30)
              .optional(),
          })
          .describe('Message body.')
          .optional(),
        inboxId: z
          .string()
          .describe('Message Id in the Inbox')
          .max(40)
          .optional()
          .nullable(),
        visitorOptions: z.array(z.string()).max(10).optional(),
        assistantActions: z.array(z.string()).max(10).optional(),
        visitorOptionsFields: z
          .array(z.record(z.string(), z.any()))
          .max(50)
          .optional(),
        engineStatus: z
          .string()
          .describe('Exact status message returned by ai site chat engine,')
          .max(50)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const MediaAttachmentUploadUrlRequest = z.object({
  mimeType: z.string().max(100),
  options: z
    .object({
      fileName: z.string().max(100).optional().nullable(),
      parentFolderId: z.string().max(100).optional().nullable(),
      filePath: z.string().max(100).optional().nullable(),
    })
    .optional(),
});
export const MediaAttachmentUploadUrlResponse = z.object({
  uploadUrl: z.string().url().optional(),
});
export const BulkGetByInboxMessagesRequest = z.object({
  inboxConversationId: z
    .string()
    .describe('inboxConversationId of the conversation to get messages by.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      inboxMessageSequences: z.array(z.string()).min(1).max(100).optional(),
    })
    .optional(),
});
export const BulkGetByInboxMessagesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('ItemMetadata')
          .optional(),
        message: z
          .object({
            _id: z
              .string()
              .describe('Message ID.')
              .max(40)
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the message was created.')
              .optional()
              .nullable(),
            sender: z
              .enum(['ASSISTANT', 'USER', 'SYSTEM', 'SITE_VISITOR'])
              .describe('Message sender type.')
              .optional(),
            conversationId: z
              .string()
              .describe('Conversation ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            answerTo: z
              .string()
              .describe('ID of the message that this message is answering.')
              .max(40)
              .optional()
              .nullable(),
            messageType: z
              .enum([
                'QUESTION',
                'ANSWER',
                'INTRO',
                'LEGAL',
                'CONTACT_FORM',
                'CONTACT_FORM_SUBMITTED',
                'SITE_OWNER_NOTIFICATION',
                'OFFLINE',
                'OUT_OF_QUOTA',
                'USER_ANSWER',
                'SITE_NAVIGATION',
                'NAVIGATION_ANSWER',
                'IGNORED_ANSWER',
                'VISITOR_OPTION_SELECTED',
                'ADD_TO_CART',
                'BOOK_SERVICE',
                'GENERATED_QUESTION',
                'SUGGESTED_QUESTION',
                'USER_QUESTION',
                'USER_QUESTION_ANSWER',
                'WIX_USER_MEDIA',
                'MEDIA',
                'WIX_USER_CARD',
                'WIX_USER_MINIMAL_BODY',
                'WIX_USER_FORM',
                'WIX_SYSTEM',
              ])
              .describe('Message type.')
              .optional(),
            body: z
              .object({
                mainText: z
                  .string()
                  .describe('Main text of the message.')
                  .max(4096)
                  .optional()
                  .nullable(),
                footerText: z
                  .string()
                  .describe('Footer text of the message.')
                  .max(4096)
                  .optional()
                  .nullable(),
                additionalData: z
                  .array(z.record(z.string(), z.any()))
                  .max(30)
                  .optional(),
              })
              .describe('Message body.')
              .optional(),
            inboxId: z
              .string()
              .describe('Message Id in the Inbox')
              .max(40)
              .optional()
              .nullable(),
            visitorOptions: z.array(z.string()).max(10).optional(),
            assistantActions: z.array(z.string()).max(10).optional(),
            visitorOptionsFields: z
              .array(z.record(z.string(), z.any()))
              .max(50)
              .optional(),
            engineStatus: z
              .string()
              .describe('Exact status message returned by ai site chat engine,')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe('Message object if found, otherwise empty.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'BulkActionMetadata.\n\nIf message was not found, still counts as success.\nFailure to retrieve a message will result in a failure count.'
    )
    .optional(),
});
